import {Box, Flex, Text} from 'exsportia-components'
import {useFormikContext} from 'formik'
import {DateTime} from 'luxon'
import React, {FC, useCallback, useMemo} from 'react'
import {useNavigate} from 'react-router-dom'

import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {SimpleGridLayoutHeader} from '../../layout/grid-layout/headers/simple-grid-layout-header'
import {CreateClassBookingFormType} from '../../services/booking/forms/create-class-booking-form'
import {SelectedSession} from '../../services/booking/forms/types'
import {DividerWithContent} from '../../ui/components/divider-with-content/divider-with-content'
import {ScrollableWrapper} from '../../ui/components/scrollable-wrapper/scrollable-wrapper'
import {useLocale} from '../../utils/i18n'

import {SessionPreviewCard} from './components/session-preview-card'

const Header: FC = () => {
  const {l} = useLocale()
  return <SimpleGridLayoutHeader title={l('approve_your_selection') || 'Approve your selection'} />
}

export const SelectedSessionsPage: FC = () => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const {values: bookingState, setValues} = useFormikContext<CreateClassBookingFormType>()
  const {selectedSessions} = bookingState

  const allSameClass = useMemo(() => {
    if (selectedSessions.length < 2) {
      return false
    }
    const firstClassId = selectedSessions[0].classId
    return selectedSessions.every((s) => s.classId === firstClassId)
  }, [selectedSessions])

  const sessionsByDate = useMemo(() => {
    const groups: Record<string, SelectedSession[]> = {}
    for (const session of selectedSessions) {
      const dateKey = session.date
      if (!groups[dateKey]) {
        groups[dateKey] = []
      }
      groups[dateKey].push(session)
    }
    return Object.entries(groups).sort(([a], [b]) => {
      const dateA = DateTime.fromFormat(a, 'dd/MM/yyyy')
      const dateB = DateTime.fromFormat(b, 'dd/MM/yyyy')
      return dateA.toMillis() - dateB.toMillis()
    })
  }, [selectedSessions])

  const handleRemoveSession = useCallback(
    (sessionId: string) => {
      const updated = selectedSessions.filter((s) => s.sessionId !== sessionId)
      if (updated.length === 0) {
        setValues({...bookingState, selectedSessions: updated})
        navigate('../select-session')
        return
      }
      setValues({...bookingState, selectedSessions: updated})
    },
    [bookingState, selectedSessions, setValues, navigate],
  )

  const handleToggleDifferentParticipants = useCallback(() => {
    setValues({
      ...bookingState,
      sharedParticipantsConfig: !bookingState.sharedParticipantsConfig,
      perSessionParticipants: {},
      handledSlots: [],
      currentSessionIndex: 0,
    })
  }, [bookingState, setValues])

  const handleNext = useCallback(() => {
    const sortedSessions = [...selectedSessions].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
    const first = sortedSessions[0]
    // Mixed classes must use per-session participants (subscriptions differ per class)
    const sharedConfig = allSameClass ? bookingState.sharedParticipantsConfig : false
    setValues({
      ...bookingState,
      selectedSessions: sortedSessions,
      sharedParticipantsConfig: sharedConfig,
      duration: first.duration,
      timestamp: first.timestamp,
      instructors: first.instructors,
      sessionId: first.sessionId,
      areaId: first.areaId,
      classId: first.classId,
      maxParticipants: first.maxParticipants,
      date: first.date,
      handledSlots: [],
      currentSessionIndex: 0,
    })
    navigate('../set-participants')
  }, [bookingState, selectedSessions, setValues, navigate, allSameClass])

  return (
    <GridLayout
      withoutPadding
      header={<Header />}
    >
      <Box>
        <ScrollableWrapper>
          <Box
            py='16px'
            px='16px'
          >
            {sessionsByDate.map(([dateKey, sessions]) => {
              const displayDate = DateTime.fromFormat(dateKey, 'dd/MM/yyyy').toFormat('ccc, MMM d, yyyy')
              return (
                <Box key={dateKey}>
                  <Box py='8px'>
                    <DividerWithContent content={displayDate} />
                  </Box>
                  {sessions.map((session) => (
                    <SessionPreviewCard
                      key={session.sessionId}
                      session={session}
                      onRemove={() => handleRemoveSession(session.sessionId)}
                      removeDisabled={selectedSessions.length <= 1}
                    />
                  ))}
                </Box>
              )
            })}
          </Box>
        </ScrollableWrapper>

        {/* Nav sits OUTSIDE the scroll: GridLayout still receives a single child (the wrapper
            Box, so the WBA-4044 single-column layout holds), but only the list scrolls — the bar
            spans full width and the scrollbar no longer eats into it or its border. WBA-4042. */}
        <Box
          bg='white'
          borderTop='1px solid'
          borderColor='#e2e8f0'
          borderRadius='0 0 24px 24px'
          p='16px'
        >
          {allSameClass && selectedSessions.length >= 2 && (
            <Flex
              alignItems='center'
              mb='12px'
              cursor='pointer'
              onClick={handleToggleDifferentParticipants}
            >
              <Box
                width='20px'
                height='20px'
                borderRadius='4px'
                border='2px solid'
                borderColor={bookingState.sharedParticipantsConfig ? '#cbd5e0' : 'primary'}
                bg={bookingState.sharedParticipantsConfig ? 'white' : 'primary'}
                mr='10px'
                display='flex'
                alignItems='center'
                justifyContent='center'
                flexShrink={0}
              >
                {!bookingState.sharedParticipantsConfig && (
                  <Text settings={{color: 'white', fontSize: '12px', fontWeight: 'bold'}}>✓</Text>
                )}
              </Box>
              <Text settings={{fontSize: '14px', color: '#4a5568'}}>
                {l('different_participants_all_sessions') || 'Different participants at all sessions'}
              </Text>
            </Flex>
          )}
          <Flex
            justifyContent='space-between'
            alignItems='center'
          >
            <Text settings={{fontSize: '14px', fontWeight: '600', color: '#4a5568'}}>
              {selectedSessions.length === 1
                ? l('session_selected_one') || '1 session selected'
                : l('session_selected_other', {value: selectedSessions.length}) ||
                  `${selectedSessions.length} sessions selected`}
            </Text>
            <Box
              cursor='pointer'
              bg='primary'
              px='24px'
              py='10px'
              borderRadius='24px'
              onClick={handleNext}
            >
              <Text settings={{color: 'white', fontWeight: 'bold', fontSize: '14px'}}>{l('next')}</Text>
            </Box>
          </Flex>
        </Box>
      </Box>
    </GridLayout>
  )
}
